/**
 * Coerces a property field that should hold a number into one, or `undefined`
 * when it holds nothing usable.
 *
 * Property numbers do not always arrive as numbers. `InputFloatingLabel` renders
 * its input through a react-hook-form `Controller`, and HeroUI's `Input` fires
 * `onChange` with a `ChangeEvent` — so the form stores `e.target.value`, a
 * string, and the `valueAsNumber` passed to `register()` never runs (it is a
 * register-only option, and the Controller's `onChange` wins). Values can also
 * come back as strings from a saved draft, a query-string prefill or a partner
 * API payload.
 *
 * The distinction that matters to callers is "usable number" vs "nothing":
 * - `''`, `'   '`, `null`, `undefined` and non-numeric text → `undefined`
 * - `NaN` and `Infinity` → `undefined` (a number input cleared by the user
 *   yields `NaN`, which survives a naive `> 0` comparison only by accident)
 * - `'1998'` → `1998`
 *
 * Note that `Number('')` is `0`, so blank input must be rejected before parsing
 * — otherwise a cleared field reads as a legitimate zero.
 */
export const toPropertyNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (trimmed === '') return undefined;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};
